import { useEditor } from '../../state/store';
import { Attr } from '../../core/types';
import { getTool } from '../../tools/registry';
import { LineStyle } from '../../unicode/boxdrawing';
import { BRUSH_BLOCKS, BRUSH_SHADES } from '../../unicode/charsets';
import { PROGRESS_PRESETS } from '../../tools/widgets';
import { WidgetKind } from '../../tools/types';
import { Checkbox, Field, Hint, NumberInput, Panel, Segmented } from '../widgets/common';
import { Select } from '../widgets/primitives';

/** The characters worth one click: the shading ramp plus the common halves. */
const QUICK_CHARS = [...BRUSH_SHADES, ' ', ...BRUSH_BLOCKS.slice(4, 10)];

const LINE_STYLES: ReadonlyArray<{ value: LineStyle; label: string; title: string }> = [
  { value: 'light', label: '─', title: 'Light' },
  { value: 'heavy', label: '━', title: 'Heavy' },
  { value: 'double', label: '═', title: 'Double' },
  { value: 'rounded', label: '╭', title: 'Rounded corners' },
  { value: 'dashed', label: '┄', title: 'Dashed' },
];

const WIDGET_KINDS: ReadonlyArray<{ value: WidgetKind; label: string }> = [
  { value: 'panel', label: 'Panel' },
  { value: 'titlebar', label: 'Title bar' },
  { value: 'separator', label: 'Separator' },
  { value: 'progress', label: 'Progress bar' },
  { value: 'button', label: 'Button' },
  { value: 'input', label: 'Input field' },
  { value: 'list', label: 'List' },
  { value: 'table', label: 'Table' },
  { value: 'scrollbar', label: 'Scrollbar' },
];

const ATTRS: ReadonlyArray<{ bit: number; label: string; title: string }> = [
  { bit: Attr.BOLD, label: 'B', title: 'Bold' },
  { bit: Attr.DIM, label: 'D', title: 'Dim' },
  { bit: Attr.ITALIC, label: 'I', title: 'Italic — not supported by every terminal' },
  { bit: Attr.UNDERLINE, label: 'U', title: 'Underline' },
  { bit: Attr.BLINK, label: '✦', title: 'Blink — often disabled in modern terminals' },
  { bit: Attr.INVERSE, label: '◑', title: 'Inverse' },
  { bit: Attr.STRIKE, label: 'S', title: 'Strikethrough' },
];

export function BrushPanel(): JSX.Element {
  const brush = useEditor((s) => s.brush);
  const setBrush = useEditor((s) => s.setBrush);
  const toolId = useEditor((s) => s.tool);
  const tool = getTool(toolId);
  const isBraille = toolId.startsWith('braille');
  const showLineStyle = toolId === 'box' || toolId === 'arrow' || toolId === 'widget';
  const showFill = toolId === 'rect' || toolId === 'ellipse' || toolId === 'box' || isBraille;

  return (
    <>
      <Panel title="Brush" collapsible={false}>
        <div className="row" style={{ marginBottom: 8 }}>
          <input
            className="input mono"
            style={{ width: 46, textAlign: 'center', fontSize: 17, height: 36 }}
            value={brush.char}
            onChange={(e) => setBrush({ char: [...e.target.value].pop() ?? ' ' })}
            title="The character this brush paints"
          />
          <div
            style={{
              flex: 1,
              display: 'grid',
              gridTemplateColumns: 'repeat(6, 1fr)',
              gap: 3,
            }}
          >
            {QUICK_CHARS.map((ch) => (
              <button
                key={ch}
                className={`char-cell${brush.char === ch ? ' selected' : ''}`}
                style={{ height: 24, fontSize: 13 }}
                onClick={() => setBrush({ char: ch })}
                title={ch === ' ' ? 'Paint blank cells' : `Use ${ch}`}
              >
                {/* A space would render as an empty button, so it gets the
                    open-box marker instead of looking like a missing swatch. */}
                {ch === ' ' ? <span className="faint">␣</span> : ch}
              </button>
            ))}
          </div>
        </div>

        <Field label="Size">
          <div className="row">
            <input
              type="range"
              min={1}
              max={9}
              value={brush.size}
              style={{ flex: 1 }}
              onChange={(e) => setBrush({ size: Number(e.target.value) })}
            />
            <span className="mono dim" style={{ width: 14, textAlign: 'right' }}>
              {brush.size}
            </span>
          </div>
        </Field>

        {/* Channel toggles sit on their own row: three labelled checkboxes do
            not fit beside a field label at this panel width. */}
        <div className="group-title" style={{ marginTop: 2 }}>
          Writes
        </div>
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <Checkbox
            label="Char"
            checked={brush.paintChar}
            onChange={(v) => setBrush({ paintChar: v })}
            title="Write the character"
          />
          <Checkbox
            label="Text"
            checked={brush.paintFg}
            onChange={(v) => setBrush({ paintFg: v })}
            title="Write the foreground colour"
          />
          <Checkbox
            label="Back"
            checked={brush.paintBg}
            onChange={(v) => setBrush({ paintBg: v })}
            title="Write the background colour"
          />
        </div>

        <div className="group-title" style={{ marginTop: 6 }}>
          Style
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 3 }}>
          {ATTRS.map((a) => (
            <button
              key={a.label}
              className={`btn icon${(brush.attrs & a.bit) !== 0 ? ' active' : ''}`}
              style={{ width: '100%', height: 26 }}
              title={a.title}
              onClick={() => setBrush({ attrs: brush.attrs ^ a.bit })}
            >
              {a.label}
            </button>
          ))}
        </div>

        <Hint>{tool.hint}</Hint>
      </Panel>

      {(showLineStyle || showFill || toolId === 'fill' || isBraille) && (
        <Panel title="Tool options" collapsible={false}>
          {showLineStyle && (
            <Field label="Line">
              <Segmented
                value={brush.lineStyle}
                options={LINE_STYLES.map((s) => ({ value: s.value, label: s.label, title: s.title }))}
                onChange={(v) =>
                  setBrush({ lineStyle: v, widgetOptions: { ...brush.widgetOptions, lineStyle: v } })
                }
              />
            </Field>
          )}
          {showFill && (
            <Checkbox label="Filled" checked={brush.filled} onChange={(v) => setBrush({ filled: v })} />
          )}
          {toolId === 'fill' && (
            <>
              <Field label="Tolerance">
                <NumberInput
                  value={brush.tolerance}
                  min={0}
                  max={255}
                  onChange={(v) => setBrush({ tolerance: v })}
                  title="0 matches exactly; higher merges similar colours"
                />
              </Field>
              <Checkbox
                label="Sample all layers"
                checked={brush.sampleComposite}
                onChange={(v) => setBrush({ sampleComposite: v })}
              />
            </>
          )}
          {isBraille && (
            <Checkbox
              label="Erase dots"
              checked={brush.brailleErase}
              onChange={(v) => setBrush({ brailleErase: v })}
              title="Right-drag erases too"
            />
          )}
        </Panel>
      )}
    </>
  );
}

/** Settings for the widget stamp tool. */
export function WidgetOptionsPanel(): JSX.Element {
  const brush = useEditor((s) => s.brush);
  const setBrush = useEditor((s) => s.setBrush);
  const w = brush.widgetOptions;
  const update = (patch: Partial<typeof w>) => setBrush({ widgetOptions: { ...w, ...patch } });

  return (
    <>
      <Panel title="Element" collapsible={false}>
        <div className="group" style={{ gap: 5 }}>
          {WIDGET_KINDS.map((k) => (
            <button
              key={k.value}
              className={`option${w.kind === k.value ? ' active' : ''}`}
              onClick={() => update({ kind: k.value })}
            >
              <span className="label">{k.label}</span>
            </button>
          ))}
        </div>
        <Hint>Pick one, then drag it out on the canvas at the size you want.</Hint>
      </Panel>

      <Panel title="Options" collapsible={false}>
        <Field label="Border">
          <Segmented
            value={w.lineStyle}
            options={LINE_STYLES.map((s) => ({ value: s.value, label: s.label, title: s.title }))}
            onChange={(v) => update({ lineStyle: v })}
          />
        </Field>

        {(w.kind === 'panel' || w.kind === 'titlebar') && (
          <Field label="Title">
            <input className="input" value={w.title} onChange={(e) => update({ title: e.target.value })} />
          </Field>
        )}

        {(w.kind === 'button' || w.kind === 'input') && (
          <Field label="Label">
            <input className="input" value={w.label} onChange={(e) => update({ label: e.target.value })} />
          </Field>
        )}

        {(w.kind === 'progress' || w.kind === 'scrollbar') && (
          <>
            <Field label="Percent">
              <div className="row">
                <input
                  type="range"
                  min={0}
                  max={100}
                  value={w.progress.percent}
                  style={{ flex: 1 }}
                  onChange={(e) => update({ progress: { ...w.progress, percent: Number(e.target.value) } })}
                />
                <span className="mono dim" style={{ width: 28, textAlign: 'right' }}>
                  {w.progress.percent}%
                </span>
              </div>
            </Field>
            <Field label="Preset">
              {/* A preset is an action, not a setting: picking one copies its
                  characters into the bar, which is then edited freely. The
                  control stays empty so it never claims to describe the bar. */}
              <Select
                ariaLabel="Preset"
                placeholder="Choose a style…"
                value=""
                options={PROGRESS_PRESETS.map((p) => ({ value: p.name, label: p.name }))}
                onChange={(name) => {
                  const preset = PROGRESS_PRESETS.find((p) => p.name === name);
                  if (preset) update({ progress: { ...w.progress, ...preset.style } });
                }}
              />
            </Field>
            <div className="row">
              <input
                className="input mono"
                style={{ width: 36, textAlign: 'center' }}
                value={w.progress.full}
                title="Filled character"
                onChange={(e) =>
                  update({ progress: { ...w.progress, full: [...e.target.value].pop() ?? '█' } })
                }
              />
              <input
                className="input mono"
                style={{ width: 36, textAlign: 'center' }}
                value={w.progress.empty}
                title="Empty character"
                onChange={(e) =>
                  update({ progress: { ...w.progress, empty: [...e.target.value].pop() ?? '░' } })
                }
              />
              <input
                className="input mono"
                style={{ width: 36, textAlign: 'center' }}
                value={w.progress.capLeft}
                title="Left cap"
                onChange={(e) => update({ progress: { ...w.progress, capLeft: e.target.value.slice(0, 1) } })}
              />
              <input
                className="input mono"
                style={{ width: 36, textAlign: 'center' }}
                value={w.progress.capRight}
                title="Right cap"
                onChange={(e) => update({ progress: { ...w.progress, capRight: e.target.value.slice(0, 1) } })}
              />
              <Checkbox
                label="%"
                checked={w.progress.showLabel}
                onChange={(v) => update({ progress: { ...w.progress, showLabel: v } })}
              />
            </div>
          </>
        )}

        {w.kind === 'table' && (
          <>
            <Field label="Columns">
              <input
                className="input"
                value={w.columns.join(', ')}
                onChange={(e) =>
                  update({ columns: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })
                }
              />
            </Field>
            <Field label="Rows">
              <textarea
                className="input"
                style={{ height: 66 }}
                value={w.items.join('\n')}
                title="One row per line, cells separated by |"
                onChange={(e) => update({ items: e.target.value.split('\n') })}
              />
            </Field>
            <Hint>One row per line; separate cells with a pipe: Alice | Online | 21 ms</Hint>
          </>
        )}

        {w.kind === 'list' && (
          <Field label="Items">
            <textarea
              className="input"
              style={{ height: 66 }}
              value={w.items.join('\n')}
              onChange={(e) => update({ items: e.target.value.split('\n') })}
            />
          </Field>
        )}

        {(w.kind === 'panel' || w.kind === 'list') && (
          <Checkbox label="Fill interior" checked={w.filled} onChange={(v) => update({ filled: v })} />
        )}
      </Panel>
    </>
  );
}
